/// <amd-module name="Wit/Common/WorkItemControls/NodeTreeDropdown" />

import * as React from "react";
import { IObservableArray, IReadonlyObservableArray, ObservableLike, ObservableValue, useObservableArray } from "VSS/Core/Observable";
import { filterMatchedItemByListboxType } from "VSSUI/Components/Dropdown/Dropdown";
import { DropdownCallout, DropdownExpandableTextField, IDropdownCalloutProps, IDropdownExpandableProps } from "VSSUI/Dropdown";
import { EditableDropdown, IEditableDropdownProps } from "VSSUI/EditableDropdown";
import { IListBoxItem, isListBoxItemVisible, ListBoxItemType } from "VSSUI/ListBox";
import { SimpleTableCell } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { ExpandableTreeCell, ITreeColumn } from "VSSUI/TreeEx";
import { css } from "VSSUI/Util";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";
import { WorkItemClassificationNode } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { INodeTreeItem, NodeTreeItemProvider } from "Wit/Common/WorkItemControls/NodeTreeItemProvider";
import { isEmpty, normalizePath } from "Wit/Common/WorkItemControls/NodeUtils";
import * as Resources from "Wit/Common/WorkItemControls/Resources";

export interface INodeTreeDropdownProps
    extends Pick<
        IEditableDropdownProps<INodeTreeItem>,
        | "ariaLabel"
        | "ariaLabelledBy"
        | "allowClear"
        | "allowFreeform"
        | "className"
        | "disabled"
        | "inputId"
        | "onValueChange"
        | "placeholder"
        | "selectedText"
        | "text"
    > {
    /**
     * Additional columns to be displayed in the tree
     */
    additionalColumns?: ITreeColumn<IListBoxItem<INodeTreeItem>>[];
    /**
     * List of classification nodes wrapped in list box items
     */
    items: IReadonlyObservableArray<IListBoxItem<INodeTreeItem>>;
    /**
     * Observable list of most recently used node ids
     */
    mruNodes?: IObservableArray<number>;
    /**
     * Callback when the path changes
     */
    onSetPath?: (newPath: string) => void;
}

/**
 * Dropdown that shows a tree of classification nodes, either area path or iteration
 */
export const NodeTreeDropdown = React.forwardRef<EditableDropdown<INodeTreeItem>, INodeTreeDropdownProps>((props, ref) => {
    const { additionalColumns, items, mruNodes, onSetPath, onValueChange, selectedText, ...rest } = props;
    const columns = props.additionalColumns ? [...defaultColumns, ...props.additionalColumns] : defaultColumns;
    const [mruNodeItems] = useObservableArray<IListBoxItem<INodeTreeItem>>(getMruItems(items.value, mruNodes?.value));
    const nodeTreeItemProvider = React.useRef(new NodeTreeItemProvider(mruNodeItems, items));
    const expandedMap = React.useRef(new Map<string, boolean>());
    const selection = React.useRef(new DropdownSelection());

    const renderCallout = (props: IDropdownCalloutProps<INodeTreeItem>) => {
        return <DropdownCallout {...props} containerClassName="node-tree-dropdown-callout" />;
    };

    const renderExpandable = (props: IDropdownExpandableProps<INodeTreeItem>) => {
        const updatedProps = {
            autoSelect: true,
            ...props
        };

        return <DropdownExpandableTextField {...updatedProps} />;
    };

    const updateSelection = (selectedText: string) => {
        // try to set the selection index of the node in the expanded tree rather than MRU items
        const expandedItems = nodeTreeItemProvider.current.value.filter(v => isListBoxItemVisible(v));
        const index = expandedItems.findIndex(item => !isMruSuggestion(item) && item.text === selectedText);
        if (index > -1) {
            selection.current.select(index);
        }
    };

    const onSelectedValueChanged = () => {
        if (!selectedText) {
            return;
        }

        // find the selected list box item node
        const text = ObservableLike.getValue(selectedText);
        const selectedItem = items.value.find(item => item.text === text);

        // iterate through and expand all of the selected item's ancestors
        let current = selectedItem?.parent;
        while (current) {
            current.expanded = true;
            expandedMap.current.set(current.id, true);
            current = current.parent;
        }

        updateSelection(text);
    };

    const onItemsChange = () => {
        mruNodeItems.value = getMruItems(items.value, mruNodes?.value);

        // update list box items expanded state based on internal toggled map.
        // this ensures that toggled state persists between updates of the node list, which the work item form tends to do a lot
        for (const item of items.value) {
            const expanded = expandedMap.current.get(item.id);
            if (expanded !== undefined) {
                item.expanded = expanded;
            }
        }

        onSelectedValueChanged();
    };

    const onToggle = (event: React.SyntheticEvent<HTMLElement, Event>, item: IListBoxItem<INodeTreeItem>) => {
        // update toggled status of the item and update selectable indices
        item.expanded = !item.expanded;
        expandedMap.current.set(item.id, item.expanded);

        if (!selectedText) {
            return;
        }

        // update the selection after toggling rows into the view
        const text = ObservableLike.getValue(selectedText);
        updateSelection(text);
    };

    React.useEffect(() => {
        const itemsCallback = items.subscribe(onItemsChange);
        const mruNodesCallback = mruNodes?.subscribe(onItemsChange);
        const selectedTextCallback = ObservableLike.isObservable(selectedText) && selectedText.subscribe(onSelectedValueChanged);

        return () => {
            items.unsubscribe(itemsCallback);

            if (mruNodes && mruNodesCallback) {
                mruNodes.unsubscribe(mruNodesCallback);
            }

            if (selectedTextCallback && ObservableLike.isObservable(selectedText)) {
                selectedText.unsubscribe(selectedTextCallback);
            }
        };
    }, [items, mruNodes, selectedText]);

    // account for selected text being passed as a string
    React.useEffect(onSelectedValueChanged, [selectedText]);

    return (
        <EditableDropdown
            allowClear={false}
            allowTextSelection
            className="node-tree-dropdown"
            columns={columns}
            filterMatchedItem={item => filterMatchedItemByListboxType(item) && !isMruSuggestion(item)}
            items={nodeTreeItemProvider.current}
            onToggle={onToggle}
            onValueChange={value => {
                if (onSetPath) {
                    if (!isEmpty(value?.data)) {
                        // try to get the path out of the node
                        const nodePath = (value!.data as WorkItemClassificationNode).path;
                        onSetPath(normalizePath(nodePath));
                    } else if (value?.text) {
                        onSetPath(value?.text);
                    } else {
                        onSetPath("");
                    }
                }

                if (onValueChange) {
                    onValueChange(value);
                }
            }}
            ref={ref}
            renderCallout={renderCallout}
            renderExpandable={renderExpandable}
            selectedText={selectedText}
            selection={selection.current}
            showTree
            minCalloutWidth={400}
            {...rest}
        />
    );
});

const defaultColumns: ITreeColumn<IListBoxItem<INodeTreeItem>>[] = [
    {
        id: "title",
        width: new ObservableValue(-100),
        renderCell: renderItemColumn
    }
];

function renderItemColumn(
    rowIndex: number,
    columnIndex: number,
    treeColumn: ITreeColumn<IListBoxItem<INodeTreeItem>>,
    treeItem: ITreeItemEx<IListBoxItem<INodeTreeItem>>
): JSX.Element {
    if (treeItem.underlyingItem.data.type === ListBoxItemType.Divider) {
        return (
            <SimpleTableCell
                className={css(treeItem.className, treeColumn.className)}
                contentClassName="divider-cell"
                columnIndex={columnIndex}
                colspan={2}
                key={treeItem.underlyingItem.id}
                tableColumn={treeColumn}
            >
                <div className="bolt-list-box-divider flex-grow" />
            </SimpleTableCell>
        );
    }

    const underlyingItem = treeItem.underlyingItem;
    const text =
        isEmpty(underlyingItem.data.data) || isMruSuggestion(underlyingItem.data)
            ? underlyingItem.text
            : (underlyingItem.data.data as WorkItemClassificationNode).name;
    return (
        <ExpandableTreeCell
            className={css(treeItem.className, treeColumn.className)}
            columnIndex={columnIndex}
            key={`title-cell-${rowIndex}-${columnIndex}`}
            treeColumn={treeColumn}
            treeItem={treeItem}
        >
            <Tooltip overflowOnly>
                <span className="text-ellipsis">{text}</span>
            </Tooltip>
        </ExpandableTreeCell>
    );
}

const suggestionsHeader: IListBoxItem = {
    id: "suggestions-header",
    text: Resources.SuggestionsHeader,
    type: ListBoxItemType.Header
};

const dividerItem: IListBoxItem = {
    id: "suggestions-divider",
    type: ListBoxItemType.Divider
};

const mruItemsGroupId = "mru-items";

function getMruItems(items: IListBoxItem<INodeTreeItem>[], mruNodes?: number[]): IListBoxItem<INodeTreeItem>[] {
    if (!mruNodes?.length || !items.length) {
        return [];
    }

    const mruListBoxItems: IListBoxItem<WorkItemClassificationNode>[] = [];
    for (const nodeId of mruNodes) {
        const matchingNode = items.find(node => !isEmpty(node?.data) && (node.data as WorkItemClassificationNode).id === nodeId);
        const nodeData = matchingNode?.data as WorkItemClassificationNode;
        if (nodeData) {
            mruListBoxItems.push({
                id: `mru-${nodeId}`,
                data: nodeData,
                text: normalizePath(nodeData.path),
                groupId: mruItemsGroupId
            });
        }
    }

    return [suggestionsHeader, ...mruListBoxItems, dividerItem];
}

function isMruSuggestion(item: IListBoxItem<INodeTreeItem>): boolean {
    return item.groupId === mruItemsGroupId;
}
